// Compare user-entered draft state, not derived credit/banners or upload progress.
// A confirmed launch must never erase a replacement draft or steal its context.
export function composerSendDraftKey(draft) {
  if (!draft) return null;
  const fields = ['text', 'selectedParameters', 'selectedButtons', 'selectedModelIds', 'selectedAgentIds',
    'botId', 'quote', 'editFileIds', 'agentSelectionTouched', 'modelSelectionTouched', 'userExplicitAutoMode'];
  return JSON.stringify([fields.map(key => draft[key]), (draft.files || []).map(file => typeof file === 'string' ? file : file?.id)]);
}

export function hasComposerDraftContent(draft) {
  return Boolean(draft?.text || draft?.files?.length || draft?.quote || draft?.editFileIds?.length);
}

function sameSession(auth, account) {
  const current = auth.getState();
  return Boolean(account.user?.id && current.token && !current._isSigningOut &&
    current.user?.id === account.user.id && current._sessionGeneration === account._sessionGeneration);
}
export function retainComposerSendDraft({ store, auth, contextId }) {
  const account = auth.getState(), userId = account.user?.id, generation = account._sessionGeneration;
  const expectedDraft = composerSendDraftKey(store.getState().inputStates[contextId]);
  const confirm = accepted => {
    const current = auth.getState();
    if (accepted === true && userId && current.token && !current._isSigningOut &&
        current.user?.id === userId && current._sessionGeneration === generation && expectedDraft !== null)
      store.getState().clearInput(false, { contextId, expectedDraft });
    return accepted === true;
  };
  return result => result && typeof result.then === 'function'
    ? Promise.resolve(result).then(confirm, () => false)
    : confirm(result);
}

// Capture before invoking the handler: it can navigate or change contexts even
// before returning its promise. Failed dispatch never writes over a newer draft.
export function dispatchComposerSendDraft(options, send) {
  const settle = retainComposerSendDraft(options);
  const { store, auth, contextId, optimistic = false } = options;
  const account = auth.getState();
  const draft = store.getState().inputStates[contextId];
  try {
    const result = send();
    // Mobile Work has already rendered its pending message, but task startup
    // can take seconds. Clear now and retain the COMPLETE draft for rollback.
    // Other composers keep their existing acceptance-based behavior.
    if (optimistic && result && typeof result.then === 'function' && draft && sameSession(auth, account) &&
        store.getState().clearInput(false, { contextId, expectedDraft: composerSendDraftKey(draft) })) {
      const clearedDraft = composerSendDraftKey(store.getState().inputStates[contextId]);
      const finish = accepted => {
        if (accepted !== true && sameSession(auth, account)) {
          store.getState().recoverSendDraft(contextId, draft, clearedDraft);
        }
        return accepted === true;
      };
      return Promise.resolve(result).then(finish, () => finish(false));
    }
    // Legacy synchronous handlers accept by returning void. Async handlers must
    // explicitly confirm acceptance before the captured draft can be cleared.
    return settle(result && typeof result.then === 'function' ? result : result !== false);
  } catch (_) {
    return false;
  }
}
